import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import Button from '../../components/Button'
import Input from '../../components/Input'
import Toast from '../../components/Toast'
import Icon from '../../components/Icon'
import { api } from '../../lib/api'

export default function ChangePasswordPage() {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  })
  const [toast, setToast] = useState(null)

  const changePasswordMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/api/snagmatic/auth/change-password', data)
      return response.data
    },
    onSuccess: () => {
      setToast({
        type: 'success',
        message: 'Password changed successfully!',
        duration: 3000
      })
      setTimeout(() => navigate('/settings'), 2000)
    },
    onError: (error) => {
      setToast({
        type: 'error',
        message: error.response?.data?.message || 'Failed to change password',
        duration: 4000
      })
    }
  })

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    // Validation
    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      setToast({
        type: 'error',
        message: 'All fields are required',
        duration: 3000
      })
      return
    }

    if (formData.newPassword.length < 8) {
      setToast({
        type: 'error',
        message: 'Password must be at least 8 characters',
        duration: 3000
      })
      return
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setToast({
        type: 'error',
        message: 'New passwords do not match',
        duration: 3000
      })
      return
    }

    changePasswordMutation.mutate(formData)
  }

  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }))
  }

  return (
    <div className="min-h-screen bg-surface">
      <div className="max-w-md mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/settings')}
            className="flex items-center gap-2 text-accent hover:text-accent-light transition mb-4"
          >
            <Icon name="arrow_back" className="text-lg" />
            Back to Settings
          </button>
          <h1 className="text-headline-lg text-text-primary">Change Password</h1>
          <p className="text-body-sm text-text-secondary mt-2">
            Update your password to keep your account secure
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Current Password */}
          <div>
            <label className="block text-label-sm text-text-muted mb-2">
              Current Password
            </label>
            <div className="relative">
              <input
                type={showPassword.current ? 'text' : 'password'}
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleChange}
                placeholder="Enter your current password"
                className="w-full px-4 py-2 border border-border rounded-lg bg-surface-high text-text-primary placeholder-text-muted focus:outline-none focus:border-accent transition"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('current')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary transition"
              >
                <Icon name={showPassword.current ? 'visibility_off' : 'visibility'} className="text-lg" />
              </button>
            </div>
          </div>

          {/* New Password */}
          <div>
            <label className="block text-label-sm text-text-muted mb-2">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPassword.new ? 'text' : 'password'}
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                placeholder="Enter a new password (min 8 characters)"
                className="w-full px-4 py-2 border border-border rounded-lg bg-surface-high text-text-primary placeholder-text-muted focus:outline-none focus:border-accent transition"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('new')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary transition"
              >
                <Icon name={showPassword.new ? 'visibility_off' : 'visibility'} className="text-lg" />
              </button>
            </div>
            {formData.newPassword && (
              <p className={`text-label-xs mt-1 ${
                formData.newPassword.length >= 8 ? 'text-status-success' : 'text-status-warning'
              }`}>
                {formData.newPassword.length >= 8 ? '✓ Password is strong' : '⚠ At least 8 characters required'}
              </p>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-label-sm text-text-muted mb-2">
              Confirm New Password
            </label>
            <div className="relative">
              <input
                type={showPassword.confirm ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your new password"
                className="w-full px-4 py-2 border border-border rounded-lg bg-surface-high text-text-primary placeholder-text-muted focus:outline-none focus:border-accent transition"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary transition"
              >
                <Icon name={showPassword.confirm ? 'visibility_off' : 'visibility'} className="text-lg" />
              </button>
            </div>
            {formData.confirmPassword && (
              <p className={`text-label-xs mt-1 ${
                formData.newPassword === formData.confirmPassword ? 'text-status-success' : 'text-status-urgent'
              }`}>
                {formData.newPassword === formData.confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => navigate('/settings')}
              className="flex-1 px-4 py-3 border border-border text-text-primary rounded-lg hover:bg-surface-high transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={changePasswordMutation.isPending}
              className="flex-1 px-4 py-3 bg-accent text-surface-dark font-semibold rounded-lg hover:bg-accent-light transition disabled:opacity-40 flex items-center justify-center gap-2"
            >
              {changePasswordMutation.isPending ? (
                <>
                  <span className="inline-block w-4 h-4 border-2 border-surface-dark border-t-transparent rounded-full animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Icon name="lock" className="text-lg" />
                  Change Password
                </>
              )}
            </button>
          </div>
        </form>

        {/* Security tip */}
        <div className="mt-8 p-4 bg-surface-high border border-border rounded-lg">
          <div className="flex gap-3">
            <Icon name="info" className="text-accent text-lg flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-label-sm font-semibold text-text-primary">Security Tip</p>
              <p className="text-body-xs text-text-secondary mt-1">
                Use a strong password with a mix of uppercase, lowercase, numbers, and symbols. Never reuse passwords across different accounts.
              </p>
            </div>
          </div>
        </div>
      </div>

      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
          duration={toast.duration}
        />
      )}
    </div>
  )
}
